import _, {isNumber} from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {UnreachableError} from '../../../../../../libs/shared/core/src/helpers/types/customErrors';
import {PaletteColorsEnum} from '../../../../../../libs/shared/core/src/models/api/paletteModel';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {HighlightTypesEnum} from '../../../../../models/api/highlightBaseModel';
import {TagModel} from '../../../../../models/api/tagModel';
import {getStarredTagIds} from '../../../../../store/data/tags/tagsSelectors';
import {CellStarIcon} from './cellStarIcon';
import {CellTagColor} from './cellTagColor';
import {CellTagEmoji} from './cellTagEmoji';

/*
 * Props.
 */

interface CellTagsProps {
  tags: ReadonlyArray<TagModel>;
  isSelected?: boolean;
  maxTagsToShow?: number;
  isStarred?: boolean;
}

/*
 * Style.
 */

const StyledTagsRemainder = styled.div<{$isSelected?: boolean}>`
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  font-size: ${fontSizes.veryTiny};
  font-weight: ${fontWeights.semibold};
  color: ${(p) => (p.$isSelected ? p.theme.invertedText : p.theme.greys.shade70)};
  background-color: ${(p) => (p.$isSelected ? p.theme.alphas.white30 : p.theme.greys.shade30)};
  border-radius: 2px;
  height: 16px;
  padding: 3px;
  min-width: 16px;
`;

/*
 * Component.
 */

export const CellTags: FC<CellTagsProps> = (props) => {
  const starredTagIds = useAppSelector(getStarredTagIds);
  const tagsRemainderCount = isNumber(props.maxTagsToShow) ? props.tags.length - props.maxTagsToShow : 0;

  return (
    <>
      {_(props.tags)
        .orderBy([
          (t) => t.type === HighlightTypesEnum.EMOJI,
          (t) => starredTagIds.has(t.id),
          (t) => t.type === HighlightTypesEnum.COLOR,
        ])
        .map((t) => {
          switch (t.type) {
            case HighlightTypesEnum.EMOJI:
              return <CellTagEmoji key={t.id} tag={t} />;

            case HighlightTypesEnum.COLOR:
              return <CellTagColor key={t.id} name={t.name} color={t.highlightColor} />;

            case HighlightTypesEnum.NONE:
              return <CellTagColor key={t.id} name={t.name} color={PaletteColorsEnum.GREY} />;

            default:
              throw new UnreachableError(t);
          }
        })
        .take(props.maxTagsToShow ? props.maxTagsToShow : props.tags.length)
        .value()}
      {props.isStarred && <CellStarIcon />}
      {tagsRemainderCount > 0 && (
        <StyledTagsRemainder $isSelected={props.isSelected}>+{tagsRemainderCount}</StyledTagsRemainder>
      )}
    </>
  );
};
